// Shared helpers for the viewer-controlled CBAM certificate price on the public
// portal. The price is an ESTIMATE the declarant sets — never the operator —
// and it drives the table's CBAM Cost column, the estimator, and the per-row
// breakdown popover via useCalculateStore.carbonPrice.
//
// Resolution order on load: URL ?price= → localStorage → default.

// Seed value for a viewer who has chosen nothing yet (no ?price=, no stored
// choice). Deliberately a constant and not the live Q1 average: it is read
// synchronously at mount, before any request can have answered, and letting a
// later fetch overwrite it would move the viewer's cost figures under them.
export const DEFAULT_CERTIFICATE_PRICE = 75.36;

// The two ends of the plausible range. These are judgement calls, not market
// data, which is why they carry no year and no source — unlike the quarter
// averages below, which come from EEX.
export const CERTIFICATE_PRICE_BOUNDS = {
  lower: { value: 65, label: 'Lower estimate' },
  upper: { value: 80, label: 'Upper estimate' },
};

/**
 * Suggested certificate prices for the ControlWithTooltip hover dropdown.
 *
 * The quarter rows are EEX reference prices fetched from the backend — see
 * `hooks/usePortalCertificatePrices`. They used to be a single hardcoded
 * `{ value: 75.36, label: 'Q1 Average Price' }`, which was correct until the
 * Commission published the next average and then quietly wrong forever.
 *
 * Every row here is an OFFICIAL published average, straight from
 * `EEXQuarterlyPrice`. Nothing is derived: a mean of the weekly closes is not how
 * the official quarterly average is computed, so a computed stand-in would be a
 * different figure wearing the same name. A quarter we hold no record for simply
 * has no row.
 *
 * Hence no `isProjected` marker — there is no estimate to mark. Anything the
 * backend sends that is not `confirmed` is dropped rather than rendered, so a
 * future change on that side cannot quietly put an unofficial number in front of
 * a customer's customer.
 *
 * Bounds sit outside the quarters so the list always reads low → quarters → high.
 */
export const buildCertificatePricePresets = (quarters = []) => {
  const quarterOptions = (Array.isArray(quarters) ? quarters : [])
    .filter(
      (q) =>
        q?.status === 'confirmed' &&
        // `Number(null)` is 0, not NaN — guarding on Number.isFinite alone would
        // turn "this quarter has no price" into a suggestion of €0.
        q?.averagePrice !== null &&
        q?.averagePrice !== undefined &&
        q?.averagePrice !== '' &&
        Number.isFinite(Number(q.averagePrice)),
    )
    .map((q) => ({
      value: Number(q.averagePrice),
      label: `${q.quarter} Average Price`,
      desc: `Official EEX average, ${q.year}`,
    }));

  return [CERTIFICATE_PRICE_BOUNDS.lower, ...quarterOptions, CERTIFICATE_PRICE_BOUNDS.upper];
};

export const PRICE_QUERY_PARAM = 'price';
const STORAGE_KEY = 'cg_public_certificate_price';

// Reject NaN / negatives and cap absurd values so a stray URL param can't blow
// up the cost figures.
export const clampCertificatePrice = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(n, 100000);
};

export const readStoredCertificatePrice = () => {
  try {
    return clampCertificatePrice(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

export const persistCertificatePrice = (value) => {
  const n = clampCertificatePrice(value);
  if (n == null) return;
  try {
    localStorage.setItem(STORAGE_KEY, String(n));
  } catch {
    // Storage can be unavailable (private mode / disabled cookies) — the URL
    // param still carries the value, so this is a best-effort convenience.
  }
};

export const readUrlCertificatePrice = (search) => {
  try {
    const params = new URLSearchParams(
      search ?? (typeof window !== 'undefined' ? window.location.search : ''),
    );
    return clampCertificatePrice(params.get(PRICE_QUERY_PARAM));
  } catch {
    return null;
  }
};

export const resolveInitialCertificatePrice = () => {
  const fromUrl = readUrlCertificatePrice();
  if (fromUrl != null) return fromUrl;
  const stored = readStoredCertificatePrice();
  if (stored != null) return stored;
  return DEFAULT_CERTIFICATE_PRICE;
};
